import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { School, SchoolStatus } from './school.types';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/utils/api-response';

@Injectable({ providedIn: 'root' })
export class SchoolSharedService {
    /**
     * Private API URL
     */
    private API_URL = environment.apiUrl;

    /**
     * Private BehaviorSubjects for state management
     */
    private _school = new BehaviorSubject<School | null>(null);
    private _schools = new BehaviorSubject<School[] | null>(null);

    /**
     * Public Observables for components to subscribe to
     */
    readonly school$ = this._school.asObservable();
    readonly schools$ = this._schools.asObservable();

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    /**
     * Get all schools
     */
    getSchools(): Observable<ApiResponse<School[]>> {
        return this._httpClient
            .get<ApiResponse<School[]>>(`${this.API_URL}/api/v1/schools`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._schools.next(response.data);
                    }
                }),
                catchError(this.handleError)
            );
    }

    /**
     * Get all schools
     */
    getUserSchools(): Observable<ApiResponse<School[]>> {
        return this._httpClient
            .get<ApiResponse<School[]>>(`${this.API_URL}/api/v1/schools`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._schools.next(response.data);
                    }
                }),
                catchError(this.handleError)
            );
    }

    /**
     * Get school by code
     */
    getSchoolByCode(code: string): Observable<ApiResponse<School>> {
        return this._httpClient
            .get<ApiResponse<School>>(`${this.API_URL}/schools/${code}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._school.next(response.data);
                    }
                }),
                catchError(this.handleError)
            );
    }

    /**
     * Create new school
     */
    createSchool(school: School): Observable<ApiResponse<School>> {
        return this.schools$.pipe(
            take(1),
            switchMap((schools) =>
                this._httpClient
                    .post<ApiResponse<School>>(`${this.API_URL}/api/v1/schools`, school)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                this._schools.next([response.data, ...(schools || [])]);
                            }
                        }),
                        catchError(this.handleError)
                    )
            )
        );
    }

    /**
     * Update school
     */
    updateSchool(code: string, school: School): Observable<ApiResponse<School>> {
        return this.schools$.pipe(
            take(1),
            switchMap((schools) =>
                this._httpClient
                    .put<ApiResponse<School>>(`${this.API_URL}/api/v1/schools/${code}`, school)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data && schools) {
                                const index = schools.findIndex((item) => item.code === code);
                                if (index !== -1) {
                                    schools[index] = response.data;
                                    this._schools.next([...schools]);
                                    this._school.next(response.data);
                                }
                            }
                        }),
                        catchError(this.handleError)
                    )
            )
        );
    }

    /**
     * Update school status
     */
    updateSchoolStatus(code: string, status: SchoolStatus): Observable<ApiResponse<School>> {
        return this.schools$.pipe(
            take(1),
            switchMap((schools) =>
                this._httpClient
                    .patch<ApiResponse<School>>(`${this.API_URL}/api/v1/schools/${code}/status`, { status })
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data && schools) {
                                const index = schools.findIndex((item) => item.code === code);
                                if (index !== -1) {
                                    schools[index] = response.data;
                                    this._schools.next([...schools]);
                                    this._school.next(response.data);
                                }
                            }
                        }),
                        catchError(this.handleError)
                    )
            )
        );
    }

    /**
     * Delete school
     */
    deleteSchool(code: string): Observable<ApiResponse<boolean>> {
        return this.schools$.pipe(
            take(1),
            switchMap((schools) =>
                this._httpClient
                    .delete<ApiResponse<boolean>>(`${this.API_URL}/api/v1/schools/${code}`)
                    .pipe(
                        tap((response) => {
                            if (response.success && schools) {
                                const index = schools.findIndex((item) => item.code === code);
                                if (index !== -1) {
                                    schools.splice(index, 1);
                                    this._schools.next([...schools]);
                                }
                            }
                        }),
                        catchError(this.handleError)
                    )
            )
        );
    }

    /**
     * Error handler
     */
    private handleError(error: any) {
        console.error('An error occurred:', error);
        const errorResponse: ApiResponse<any> = error?.error || {
            success: 0,
            message: 'An unexpected error occurred',
            data: null,
            timestamp: new Date()
        };
        return throwError(() => errorResponse);
    }
}
